/**
 * OpenAI — gpt-image-1 (ChatGPT Image 2). /v1/images/generations and /v1/images/edits.
 *
 * "ChatGPT Image 2" in the user's spec refers to the gpt-image model family;
 * the canonical endpoint for image generation is /v1/images/generations and
 * for edits is /v1/images/edits. If/when OpenAI publishes "gpt-image-2" we
 * just bump the model string below.
 */
import type { GenerateRequest, GenerateResult, ProviderAdapter, ModelDescriptor } from '../router/types';
import { proxy, ok, fail } from './common';

const BASE = 'https://api.openai.com/v1';

export const OPENAI_MODELS: ModelDescriptor[] = [
  {
    id: 'gpt-image-1',
    label: 'ChatGPT Image',
    mode: 'image',
    caps: { multiRef: 4, edit: true, aspects: ['1:1','3:4','4:3','9:16','16:9'], maxRes: '2k' }
  }
];

function aspectToSize(a?: string): string {
  switch (a) {
    case '1:1': return '1024x1024';
    case '3:4': return '768x1024';
    case '4:3': return '1024x768';
    case '9:16': return '1024x1792';
    case '16:9': return '1792x1024';
    default: return '1024x1024';
  }
}

async function generate(req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
  const endpoint = `${BASE}/images/generations`;
  const body = {
    model: 'gpt-image-1',
    prompt: req.prompt,
    size: aspectToSize(req.aspect),
    n: 1,
    quality: 'high',
    background: req.providerOpts?.transparent ? 'transparent' : 'opaque',
    response_format: 'b64_json'
  };
  const res = await proxy<any>({ provider: 'openai', endpoint, body, apiKey, authStyle: 'bearer' });
  const data = res.data ?? [];
  if (!data.length) return fail('No image returned', res);
  return ok(data.map((d: any) => ({
    url: `data:image/png;base64,${d.b64_json}`,
    mime: 'image/png'
  })));
}

async function edit(req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
  // Multipart needs to be assembled server-side. We pass URLs + prompt to proxy
  // and the proxy turns it into a multipart form. See netlify/functions/proxy.ts.
  const endpoint = `${BASE}/images/edits`;
  const body = {
    model: 'gpt-image-1',
    prompt: req.prompt,
    size: aspectToSize(req.aspect),
    image_urls: (req.refs ?? []).map(r => r.url),
    mask_url: req.refs?.find(r => r.role === 'mask')?.url,
    n: 1,
    quality: 'high'
  };
  const res = await proxy<any>({ provider: 'openai', endpoint, body, apiKey, authStyle: 'bearer' });
  const data = res.data ?? [];
  if (!data.length) return fail('No image returned', res);
  return ok(data.map((d: any) => ({
    url: `data:image/png;base64,${d.b64_json}`,
    mime: 'image/png'
  })));
}

export const openai: ProviderAdapter = {
  id: 'openai',
  models: OPENAI_MODELS,
  async generate(_modelId: string, req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
    if ((req.refs?.length ?? 0) > 0) return edit(req, apiKey);
    return generate(req, apiKey);
  }
};